import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import _keyBy from 'lodash/keyBy'
import ACCESS from 'utils/Access'
import moment from 'moment'
import IntegrationBreadcrumb from 'components/Integration/components/IntegrationBreadcrumb'
import withNotifications from 'components/shared/notifications'
import { getSalesforceToggles, patchSalesforceToggles } from 'app/modules/integrations/api'
import { api } from 'app/modules/sfdc/api'
import IntegrationToggles from 'components/IntegrationToggles'
import SFDCAccountSyncModal from 'components/SFDC/SFDCAccountSyncModal'
import AddButton from 'components/Integration/components/AddButton/index'
import { model, hydrateModel } from './model'
import { Button, Icon, Tag, Modal, Input } from '@driftt/tide-core-legacy'
import { equals } from 'ramda'

const CONNECT_ERROR_MESSAGE = 'We could not fetch your settings.'
const PATCH_ERROR_MESSAGE = 'We could not save your changes.'

class SalesforceConfig extends Component {

  static contextTypes = {
    router: PropTypes.object.isRequired
  }

  state = {
    toggles: model,
    accountToggles: [],
    lists: [],
    savedLists: [],
    syncHistory: {},
    showAccountsSyncModal: false,
    warningModal: false,
    checked: false
  }

  componentDidMount() {
    document.getElementsByClassName('app-content-container')[0].scrollTo(0,0)
    
    api.fetchSFDCAccountListSyncHistory()
      .then(this.receiveHistory)
      .then(this.startSyncPoll)

    api.fetchSFDCAccountLists()
      .then(this.receiveLists)
      .catch(this.onError)

    api.fetchSyncedSFDCAccountLists()
      .then(this.receiveSavedLists)
      .catch(this.onError)

    getSalesforceToggles()
      .then(this.receiveToggles)
      .catch(this.onError)
  }

  componentWillUnmount() {
    clearInterval(this.syncInterval)
  }

  startSyncPoll = () => {
    const getSyncInfo = () => {
      api.fetchSFDCAccountListSyncHistory()
        .then(this.receiveHistory)
    }

    this.syncInterval = setInterval(getSyncInfo, 10000)
  }

  onError = () => {
    const { router } = this.context

    this.props.notifications.error(CONNECT_ERROR_MESSAGE)
    router.push({ pathname: '/settings/setup/salesforce' })
  }

  onPatchError = () => {
    this.props.notifications.error(PATCH_ERROR_MESSAGE)
  }

  resync = () => {
    api.resyncAccountList()
      .then(() => this.props.notifications.success(' We are re-syncing your accounts'))
      .catch(() => this.props.notifications.error(' There were errors syncing your accounts lists'))
  }

  forceUpdateSync= () => {
    api.setForceResync()
      .then(() => {
        api.resyncAccountList()
          .then(() => this.props.notifications.success(' We are updating all of your accounts'))
      })
      .catch(() => this.props.notifications.error(' There were errors syncing your accounts lists'))
  }


  receiveHistory = resp => {
    this.setState({
      syncHistory: resp
    })
  }

  receiveToggles = resp => {
    this.setState({
      toggles: hydrateModel(resp)
    })
  }

  settingsUpdatedNotification = () => this.props.notifications.success('Salesforce settings are updated!')

  receiveLists = resp => {
    this.setState({
      lists: resp
    })
  }

  receiveSavedLists = resp => {
    this.setState({
      savedLists: resp
    })
  }

  patchToggle = (event, toggle) => {
    if(!!toggle.confirmation && !toggle.value) {
      this.setState({
        "confirmationModalOpen" : true,
        "confirmationModalToggle" : toggle
     })
     return
    }

    const data = { [toggle.type]: event }

    patchSalesforceToggles(data)
      .then(this.receiveToggles)
      .then(this.settingsUpdatedNotification)
      .catch(this.onPatchError)
  }

  onConfirmClick = () => {
    const { confirmationModalToggle } = this.state
    const data = { [confirmationModalToggle.type]: !confirmationModalToggle.value }

    patchSalesforceToggles(data)
      .then(this.setState({ "confirmationModalOpen" : false }))
      .then(this.receiveToggles)
      .then(this.settingsUpdatedNotification)
      .catch(this.onPatchError)
  }

  onCancelClick = (val) => {
    this.setState({
      "confirmationModalOpen" : false,
   })
  }

  warningModalOnCancelClick = () =>  {
    this.setState(prevState => ({
      'warningModal' : !prevState.warningModal
    }))
  }

  handleCheckboxChange = () => {
    this.setState(prevState => ({
      'checked' :  !prevState.checked
    }))
  }

  setAccountDeleteOk = (event) => {

    api.setAccountDeleteOk(event)
      .then(this.receiveAccountToggles)
      .then(this.settingsUpdatedNotification)
      .catch(this.onPatchError)
  }

  toggleAccountsSyncModal = () => {
    this.setState(prevState => ({
      showAccountsSyncModal: !prevState.showAccountsSyncModal
    }))
  }

  deleteSyncedAccountList = id => {
    api.deleteSyncedSFDCAccountList(id)
      .then(this.receiveSavedLists)
      .catch(this.onPatchError)
  }

  saveNewAccountLists = (accountLists) => {
    const savedAndNewAccountLists = [...accountLists, ...this.state.savedLists]

    api.saveSyncedSFDCAccountLists(savedAndNewAccountLists)
      .then(this.receiveSavedLists)
      .then(this.toggleAccountsSyncModal)
      .then(this.props.notifications.success("We're syncing your Salesforce Accounts. Please check back soon."))
      .catch(this.onPatchError)
  }

  renderSyncedList = (list) => {
    return (
      <tr key={list.accountListId}>
        <td>
          {list.accountListName}
        </td>
        <td>
          {list.targeted &&
            <Tag text="Targeted" />
          }
        </td>
        <td>
          <span onClick={() => this.deleteSyncedAccountList(list.accountListId)}>
            <Icon width={15} name="trash"/>
          </span>
        </td>
      </tr>
    )
  }

  renderSyncedAccountLists = () => {
    const { savedLists } = this.state
    if (!savedLists.length) return <p>You haven’t synced any accounts from Salesforce yet. Today seems like a good day for it :)</p>

    return (
      <table className="drift-table salesforce-account-list-table">
        <tbody>
          {savedLists
            .map(this.renderSyncedList)
          }
        </tbody>
      </table>
    )
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !equals(nextState, this.state)
  }

  renderConfirmationModal = (toggle) => {
    return (
      <Modal
        open={ true }
        onClose={ this.onCancelClick }
        className="toggle-confirmation-modal"
        size="small"
      >
        <p className="sfdc-records-header">Are you sure you want to always create new Salesforce records?</p>
        <p className="sfdc-records-message-body">{ toggle.confirmation }</p>
        <div className="button-container">
          <Button onClick={ this.onConfirmClick } type="secondary">
            Continue
          </Button>
          <Button onClick={ this.onCancelClick } className="toggle-cancel-button" type="tertiary">
            Cancel
          </Button>
        </div>
      </Modal>
    )
  }

  renderWarningModal = () => {
    return (
      <Modal
        open={true}
        className="warning-modal"
        size="small"
        onClose={ this.warningModalOnCancelClick }
      >
        <h3>Before you sync SFDC Accounts</h3>
        <p>Please make sure your Drift and Salesforce users are properly mapped on
          <a href="https://app.drift.com/settings/salesforce/mapping/users" target="_blank"> https://app.drift.com/settings/salesforce/mapping/users </a>
          before attempting to sync Accounts from Salesforce into Drift.
          Without mapped users, the account sync will fail.</p>
        <div className="button-container">
          <Button type="secondary" onClick={(event) => {this.toggleAccountsSyncModal(event); this.warningModalOnCancelClick(event)}}>Sync</Button>
          <Button type="tertiary" onClick={this.warningModalOnCancelClick}>Cancel</Button>
          <Input
            className="warning-modal-checkbox"
            type="checkbox"
            checked={this.handleCheckboxChange}
            label="Don’t warn me again"/>
        </div>
      </Modal>
    )
  }

  render() {
    const { toggles, showAccountsSyncModal, lists, savedLists, syncHistory, accountToggles, confirmationModalOpen, confirmationModalToggle, warningModal } = this.state
    const hasErrors = syncHistory.results && (syncHistory.results.FAILED_NODOMAIN.length || syncHistory.results.FAILED_NOOWNER.length)

    return (
      <div className="common-integration-page-layout">
        <IntegrationBreadcrumb
          backLinkText="Salesforce Integration"
          backLinkUrl="/settings/setup/salesforce"
        />
        <header>
          <h2 className="integration-header">Sync Settings</h2>
          <p>Choose which Salesforce objects are sent between Drift and Salesforce.</p>
        </header>

        <h3 className="integration-header">
          Drift
          <Icon
            className="right-arrow"
            width={15}
            height={10}
            name="arrow-2-right"
          />
          Salesforce
        </h3>
        <IntegrationToggles
          toggles={toggles}
          onChange={this.patchToggle}
        />

        {ACCESS.hasGates('ABM') &&
          <div className="salesforce-account-sync-config">
            <h3 className="integration-header" >
              Salesforce
              <Icon
                className="right-arrow"
                width={15}
                height={10}
                name="arrow-2-right"
              />
              Drift
            </h3>

            <div className="salesforce-account-sync-config-container">
              <h2 className="integration-header" >Synced Account Lists</h2>
              <p>
              Sync your accounts from Salesforce to target with Drift ABM or other playbooks. Drift will keep your accounts in sync with Salesforce and refresh daily.
                <a target="_blank" href="/accounts">&nbsp;Click here to go to accounts.</a>
              </p>

              <IntegrationToggles
                toggles={accountToggles}
                onChange={this.setAccountDeleteOk}
              />

              <div className="sfdc-accounts-sync--title-section drift-form">
                { syncHistory.updateDateTime &&
                  <span className="account-sync-status">
                    <p>
                      Last Synced on {moment(syncHistory.updateDateTime).format('MM/DD/YYYY')} at {moment(syncHistory.updateDateTime).format('HH:MM')}
                    </p>
                    { !hasErrors &&
                      <span
                        className="account-list-resync"
                        onClick={this.resync}
                      >
                        Resync
                      </span>
                    }
                  </span>
                }
                { !!hasErrors &&
                  <span className="salesforce-account-sync-error-message">
                    <p>
                      Oh no! We ran in to some errors while syncing your Salesforce Accounts. You can fix them
                      <a href="/accounts/imports" target="_self"> here.</a>
                    </p>
                  </span>
                }
                {this.renderSyncedAccountLists()}
              </div>
              <AddButton
                addButtonLabel="Add Account List"
                type="secondary"
                onClick={syncHistory.results && _.isEmpty(syncHistory.results.CREATED) ?
                this.warningModalOnCancelClick :
                this.toggleAccountsSyncModal
                }
              />
              { !!lists.length &&
                <Button
                  className="salesforce-account-force-update"
                  type="subtle"
                  size="medium"
                  onClick={this.forceUpdateSync}
                >
                  Force Update All
                </Button>
              }
            </div>
          </div>
        }

        {showAccountsSyncModal &&
          <SFDCAccountSyncModal
            onCloseClick={this.saveNewAccountLists}
            onCancelClick={this.toggleAccountsSyncModal}
            deleteSyncedAccountList={this.deleteSyncedAccountList}
            lists={lists}
            savedLists={savedLists}
          />
        }
        {warningModal && syncHistory.results && _.isEmpty(syncHistory.results.CREATED) &&
          this.renderWarningModal()}
        {confirmationModalOpen &&
          this.renderConfirmationModal(confirmationModalToggle)
        }
      </div>
    )
  }
}

export default withNotifications(SalesforceConfig)



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/pages/SalesforceConfig/index.js